// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ApplicationInfo } from '@shared/commons/ApplicationInfo';
import { Connection } from '@shared/commons/Connection';
import { DataCache } from '@shared/commons/DataCache';
import { TvBrokerConfigFlags } from '@shared/commons/TradingViewDataFeed/BrokerConfig/TvBrokerConfigFlags';
import { TvInteriorIdCache, TvInteriorIdCacheMode } from '@shared/commons/TradingViewDataFeed/Caches/TvInteriorIdCache';
import { TvDurationConvertor } from '@shared/commons/TradingViewDataFeed/Convertors/TvDurationConvertor';
import { TvSymbolConvertor } from '@shared/commons/TradingViewDataFeed/Convertors/TvSymbolConvertor';
import { TvPriceFormatter } from '@shared/commons/TradingViewDataFeed/Formatters/TvPriceFormatter';
import { TvAccountHelper } from '@shared/commons/TradingViewDataFeed/Helpers/TvAccountHelper';
import { tvch } from '@shared/commons/TradingViewDataFeed/Helpers/TvConsoleHelper';
import { TvHistoryAggregationsHelper } from '@shared/commons/TradingViewDataFeed/Helpers/TvHistoryAggregationsHelper';
import { TvSavedDataHelper } from '@shared/commons/TradingViewDataFeed/Helpers/TvSavedDataHelper';
import { TvTimeFrameHelper } from '@shared/commons/TradingViewDataFeed/Helpers/TvTimeFrameHelper';
import { TVCustomTranslations } from '@shared/commons/TradingViewDataFeed/Localization/TvCustomTranlation';
import { TvSymbolsManager } from '@shared/commons/TradingViewDataFeed/Managers/TvSymbolsManager';
import { TvSaveWorkspaceMode, TvWorkspaceManager } from '@shared/commons/TradingViewDataFeed/Managers/TvWorkspaceManager';
import { TvDefaultWatchlistProvider } from '@shared/commons/TradingViewDataFeed/Providers/TvDefaultWatchlistProvider';
import { TvFeaturesSet } from '@shared/commons/TradingViewDataFeed/Settings/TvFeaturesSet';
import { TvWorkspaceManagerState } from '@shared/commons/TradingViewDataFeed/TradingViewPrimitives/Workspace/TvWorkspaceManagerState';
import { TvBrokerTerminal } from '@shared/commons/TradingViewDataFeed/TvBrokerTerminal';
import { TvDataFeed } from '@shared/commons/TradingViewDataFeed/TvDataFeed';
import { EventUtils } from '@shared/commons/UtilsClasses/EventUtils';
import { Resources } from '@shared/localizations/Resources';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { delay } from '@shared/utils/Time/DateTimeUtils';
var TradingPlatformAppStates;
(function (TradingPlatformAppStates) {
    TradingPlatformAppStates[TradingPlatformAppStates["NonInitialized"] = 0] = "NonInitialized";
    TradingPlatformAppStates[TradingPlatformAppStates["Inited"] = 1] = "Inited";
    TradingPlatformAppStates[TradingPlatformAppStates["ReadyToInit"] = 2] = "ReadyToInit";
})(TradingPlatformAppStates || (TradingPlatformAppStates = {}));
class SvgContainer {
}
class _TradingPlatformApp {
    constructor() {
        this.state = TradingPlatformAppStates.NonInitialized;
        this.activeChart = null;
        this.themeName = 'light';
        this.logoutSvgContainer = new SvgContainer();
        this.saveMode = TvSaveWorkspaceMode.None;
        this.isChartReady = false;
        TvInteriorIdCache.mode = TvInteriorIdCacheMode.TvTradingPlatform;
        this.createBrokerFactory = this.createBrokerFactory.bind(this);
        this.onUpdateAccount = this.onUpdateAccount.bind(this);
        this.onActiveChartChanged = this.onActiveChartChanged.bind(this);
        this.onSymbolChanged = this.onSymbolChanged.bind(this);
        TvWorkspaceManager.subscribeLoadWorkspace(this.onLoadWorkSpace.bind(this));
        TvWorkspaceManager.subscribeSaveWorkspace(this.saveTvWidget.bind(this));
        window.addEventListener('beforeunload', (event) => {
            this.saveTvWidget(TvSaveWorkspaceMode.Both).catch(() => {
                console.error('Save TV Workspace Error::');
            });
        });
    }
    Init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.state = TradingPlatformAppStates.ReadyToInit;
            const wrkState = TvWorkspaceManager.getState();
            if (wrkState === TvWorkspaceManagerState.Loaded) {
                yield this.initOnReady();
            }
            DataCache.OnUpdateInstrument.Subscribe(this.onUpdateInstrument, this);
        });
    }
    onUpdateInstrument(instrument) {
        if (instrument === TvSymbolsManager.getCurrentInstrument()) {
            void this.refreshOrderPanel();
        }
    }
    onLoadWorkSpace() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state === TradingPlatformAppStates.ReadyToInit) {
                yield this.initOnReady();
            }
        });
    }
    initOnReady() {
        return __awaiter(this, void 0, void 0, function* () {
            this.state = TradingPlatformAppStates.Inited;
            TvHistoryAggregationsHelper.parseKeys();
            const defaultAccountId = TvWorkspaceManager.getDefaultAccountId();
            TvAccountHelper.resetCurrentAccount(defaultAccountId);
            this.createDataFeed();
            // временная заглушка для тестирования, в дальнейшем нужно будет сохранять настройки
            this.cashAllSymbols();
            this.correctSavedData();
            const firstTradableInstrument = DataCache.getFirstTradableInstrument();
            TvSymbolsManager.setCurrentInstrument(firstTradableInstrument);
            let defaultSymbol = isNullOrUndefined(firstTradableInstrument) ? 'NO_AVAILABLE_SYMBOLS' : '';
            if (!isValidString(defaultSymbol) && isNullOrUndefined(TvWorkspaceManager.getChartSettings())) {
                const instrument = this.getDefaultInstrument();
                defaultSymbol = TvSymbolConvertor.getFullname(instrument);
            }
            let defaultResolution = TvWorkspaceManager.getDefaultResolution();
            if (isNullOrUndefined(defaultResolution)) {
                defaultResolution = '1D';
            }
            this.themeName = TvWorkspaceManager.getTheme();
            if (isNullOrUndefined(this.themeName)) {
                this.themeName = 'light';
            }
            const widgetOptions = {
                debug: tvch.getDebugMode(), // uncomment this line to see Library errors and warnings in the console
                fullscreen: true,
                symbol: defaultSymbol,
                interval: defaultResolution,
                container: 'tv_chart_container',
                datafeed: this.datafeed,
                library_path: '../../scripts/Libs/TradingViewTradingPlatform/charting_library/',
                locale: 'en',
                disabled_features: TvFeaturesSet.getDisabledFeatures(),
                enabled_features: TvFeaturesSet.getEnabledFeatures(),
                custom_translate_function: TVCustomTranslations.Translate,
                custom_css_url: 'custom_css/tvplatform.css',
                theme: this.themeName,
                widgetbar: {
                    details: true,
                    news: false,
                    watchlist: true,
                    datawindow: true
                    // watchlist_settings: {
                    //     default_symbols: ['MSFT', 'IBM', 'AAPL']
                    // }
                },
                // rss_news_feed: {
                //     default: [{
                //         url: 'https://demo-feed-data.tradingview.com/news?symbol={SYMBOL}',
                //         name: 'Yahoo Finance'
                //     }]
                // },
                // broker_factory: function (host) { return new Brokers.BrokerSample(host, datafeed); },
                broker_factory: this.createBrokerFactory,
                broker_config: this.getBrokerConfig(),
                time_frames: TvTimeFrameHelper.getTimeFrames(),
                settings_adapter: TvWorkspaceManager.tvSettingsAdapter,
                custom_formatters: {
                    priceFormatterFactory: (symbolInfo, minTick) => {
                        if (isNullOrUndefined(symbolInfo))
                            return null;
                        return new TvPriceFormatter(symbolInfo.name);
                    }
                }
            };
            const chartSettings = TvWorkspaceManager.getChartSettings();
            if (chartSettings) {
                widgetOptions.saved_data = chartSettings;
            }
            else {
                const defaultSymbols = yield TvDefaultWatchlistProvider.getDefaultWatchlist();
                if (defaultSymbols.length > 0) {
                    widgetOptions.widgetbar.watchlist_settings = {
                        default_symbols: defaultSymbols
                    };
                }
            }
            this.tvwidget = new TradingView.widget(widgetOptions);
            this.tvwidget.onChartReady(this.onTVWChartReady.bind(this));
            yield this.tvwidget.headerReady();
            this.afterHeaderReady();
        });
    }
    correctSavedData() {
        const chartSettings = TvWorkspaceManager.getChartSettings();
        TvSavedDataHelper.correctChartSettings(chartSettings);
        TvSavedDataHelper.correctInitialSettings(TvWorkspaceManager.tvSettingsAdapter.initialSettings);
    }
    createDataFeed() {
        this.destroyDataFeed();
        this.datafeed = new TvDataFeed();
        this.datafeed.setCurrentAccount(TvAccountHelper.getCurrentAccount());
        return this.datafeed;
    }
    createBrokerFactory(host) {
        this.destroyBrokerFactory();
        this.brokerFactory = new TvBrokerTerminal(host, this.datafeed);
        this.brokerFactory.subscribeOnUpdateAccount(this.onUpdateAccount);
        return this.brokerFactory;
    }
    destroyBrokerFactory() {
        if (isNullOrUndefined(this.brokerFactory))
            return;
        this.brokerFactory.unsubscribeOnUpdateAccount(this.onUpdateAccount);
        this.brokerFactory.destroy();
        this.brokerFactory = null;
    }
    destroyDataFeed() {
        if (isNullOrUndefined(this.datafeed))
            return;
        this.datafeed.destroy();
        this.datafeed = null;
    }
    onTVWChartReady() {
        // init broker terminal
        if (isNullOrUndefined(this.tvwidget))
            return;
        this.subscribeOnActiveChartChanged();
        this.onActiveChartChanged();
        this.subscribeIntervalChanged();
        this.isChartReady = true;
        // const chartSettings = TvWorkspaceManager.getChartSettings();
        // if (chartSettings) {
        //     this.tvwidget.load(chartSettings);
        // }
    }
    onUpdateAccount(_newAcc, _oldAcc) {
        TvWorkspaceManager.setDefaultAccountId(TvAccountHelper.getCurrentAccountId());
        this.allChartsResetData();
    }
    allChartsResetData() {
        const w = this.tvwidget;
        if (w == null) {
            return;
        }
        const numCharts = w.chartsCount();
        let chartsLoaded = 0;
        const onDataLoadedCallback = () => {
            chartsLoaded++;
            if (chartsLoaded === numCharts) {
                this.brokerFactory.updateHostCurrentAccount();
            }
        };
        for (let i = 0; i < numCharts; i++) {
            const chart = w.chart(i);
            chart.onDataLoaded().subscribe(null, onDataLoadedCallback, true);
            chart.resetData();
        }
    }
    subscribeOnActiveChartChanged() {
        this.tvwidget.subscribe('activeChartChanged', this.onActiveChartChanged);
    }
    onActiveChartChanged() {
        this.unsubscribeTvSymbolChanged();
        this.activeChart = this.tvwidget.activeChart();
        this.subscribeTvSymbolChanged();
        this.onSymbolChanged();
    }
    unsubscribeTvSymbolChanged() {
        if (isNullOrUndefined(this.activeChart))
            return;
        this.activeChart.onSymbolChanged().unsubscribe(this, this.onSymbolChanged);
    }
    subscribeTvSymbolChanged() {
        this.activeChart.onSymbolChanged().subscribe(this, this.onSymbolChanged);
    }
    onSymbolChanged() {
        const symbol = this.tvwidget.activeChart().symbol();
        const symbolWithoutExchange = InstrumentUtils.RemoveRouteName(symbol);
        const interiorId = TvInteriorIdCache.getInteriorId(symbolWithoutExchange);
        const instrument = DataCache.getInstrumentByName(interiorId);
        TvSymbolsManager.setCurrentInstrument(instrument);
    }
    subscribeIntervalChanged() {
        this.tvwidget
            .activeChart()
            .onIntervalChanged()
            .subscribe(null, () => {
            const resolution = this.tvwidget.activeChart().resolution();
            TvWorkspaceManager.setDefaultResolution(resolution);
        });
    }
    cashAllSymbols() {
        // TODO: non fixed list
        const allInstruments = DataCache.Instruments;
        for (const interiorID in allInstruments) {
            const instrument = allInstruments[interiorID];
            const name = instrument.DisplayName();
            const interiorId = instrument.GetInteriorID();
            TvInteriorIdCache.saveInteriorId(name, interiorId);
        }
    }
    getBrokerConfig() {
        const instrument = this.getDefaultInstrument();
        const account = TvAccountHelper.getCurrentAccount();
        const brokerConfig = {
            configFlags: TvBrokerConfigFlags.getConfigFlags(instrument, account),
            // customNotificationFields
            durations: TvDurationConvertor.getAllAllowedDurations()
            // positionDialogOptions
            // orderRules
            // customUI
        };
        return brokerConfig;
    }
    refreshOrderPanel() {
        return __awaiter(this, void 0, void 0, function* () {
            const isOrderPanelOpen = TvWorkspaceManager.isOrdersPanelOpen();
            if (!isOrderPanelOpen) {
                return;
            }
            const widgetbar = yield this.tvwidget.widgetbar();
            widgetbar.closeOrderPanel();
            yield delay(1000);
            widgetbar.openOrderPanel();
        });
    }
    getDefaultInstrument() {
        let ins = TvSymbolsManager.getCurrentInstrument();
        if (ins == null) {
            ins = DataCache.getAnyInstrument();
        }
        return ins;
    }
    onLogoutButtonClick() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield this.saveTvWidget(TvSaveWorkspaceMode.Both);
            this.destroyBrokerFactory();
            this.destroyDataFeed();
            DataCache.OnUpdateInstrument.UnSubscribe(this.onUpdateInstrument, this);
            this.isChartReady = false;
            // TODO:
            // dispose TV caches?
            // remove subscriptions from tv widget
            // implement IDestroyable from API
            // stop all timers
            // clear all caches
            // remove all subsciptions
            try {
                (_a = this.tvwidget) === null || _a === void 0 ? void 0 : _a.remove();
            }
            catch (e) {
                console.error(e);
            }
            Connection === null || Connection === void 0 ? void 0 : Connection.disconnect();
        });
    }
    saveTvWidget(mode) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.isChartReady) {
                return;
            }
            this.saveMode = mode;
            (_a = this.tvwidget) === null || _a === void 0 ? void 0 : _a.save(this.saveWorkSpace.bind(this));
        });
    }
    saveWorkSpace(data) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (ApplicationInfo.isExploreMode) {
                    return;
                }
                yield TvWorkspaceManager.saveWorkSpace(data, this.saveMode);
            }
            catch (e) {
                console.error('Save TV Workspace Error::', e);
            }
        });
    }
    afterHeaderReady() {
        this.createLogButton();
        this.createThemeChangedToggle();
        this.createLogoutButton();
        this.fixingIframeClipboard();
    }
    fixingIframeClipboard() {
        var _a;
        const tvIframe = (_a = document.getElementById('tv_chart_container')) === null || _a === void 0 ? void 0 : _a.firstChild;
        tvIframe.allow = 'clipboard-read; clipboard-write';
    }
    createLogoutButton() {
        const options = {
            align: 'right',
            useTradingViewStyle: false
        };
        const button = this.tvwidget.createButton(options);
        button.setAttribute('title', Resources.getResource('ribbon.enviroment.logout'));
        button.addEventListener('click', this.onLogoutButtonClick.bind(this));
        // create svg for button
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '20');
        svg.setAttribute('height', '20');
        svg.style.fill = this.themeName === 'dark' ? '#fff' : '#000';
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '../../../../assets/webdesktop/web-desktop-sprite.svg#logoutTradingView');
        svg.appendChild(use);
        button.appendChild(svg);
        this.logoutSvgContainer.svgElement = svg;
    }
    createLogButton() {
        const options = {
            align: 'right',
            useTradingViewStyle: false
        };
        const button = this.tvwidget.createButton(options);
        button.setAttribute('title', Resources.getResource('ribbon.help.downloadLogFile'));
        // create svg for button
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '20');
        svg.setAttribute('height', '20');
        svg.style.fill = this.themeName === 'dark' ? '#fff' : '#000';
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '../../../../assets/webdesktop/web-desktop-sprite.svg#downloadLogTradingView');
        svg.appendChild(use);
        button.appendChild(svg);
        button.addEventListener('click', () => {
            void EventUtils.saveLogFile();
        });
        this.logoutSvgContainer.svgElement = svg;
    }
    createThemeChangedToggle() {
        const widget = this.tvwidget;
        const logoutSvgContainer = this.logoutSvgContainer;
        const themeToggleEl = widget.createButton({
            useTradingViewStyle: false,
            align: 'right'
        });
        themeToggleEl.dataset.internalAllowKeyboardNavigation = 'true';
        themeToggleEl.id = 'theme-toggle';
        themeToggleEl.innerHTML = `
        <style>
            #documentation-toolbar-button {
                all: unset;
                position: relative;
                color: #FFF;
                font-size: 14px;
                font-weight: 400;
                line-height: 18px;
                letter-spacing: 0.15408px;
                padding: 5px 12px;
                border-radius: 80px;
                background: #2962FF;
                cursor: pointer;
            }
            #documentation-toolbar-button:hover {
                background: #1E53E5;
            }
            #documentation-toolbar-button:active {
                background: #1948CC;
            }
            #theme-toggle {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 12px;
            }
            .switcher {
                display: inline-block;
                position: relative;
                flex: 0 0 auto;
                width: 38px;
                height: 20px;
                vertical-align: middle;
                z-index: 0;
                -webkit-tap-highlight-color: transparent;
            }

            .switcher input {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                opacity: 0;
                z-index: 1;
                cursor: default;
            }

            .switcher .thumb-wrapper {
                display: block;
                border-radius: 20px;
                position: relative;
                z-index: 0;
                width: 100%;
                height: 100%;
            }

            .switcher .track {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                border-radius: 20px;
                background-color: #a3a6af;
            }

            #theme-switch:checked + .thumb-wrapper .track {
                background-color: #2962ff;
            }

            .switcher .thumb {
                display: block;
                width: 14px;
                height: 14px;
                border-radius: 14px;
                transition-duration: 250ms;
                transition-property: transform;
                transition-timing-function: ease-out;
                transform: translate(3px, 3px);
                background: #ffffff;
            }

            [dir=rtl] .switcher .thumb {
                transform: translate(-3px, 3px);
            }

            .switcher input:checked + .thumb-wrapper .thumb {
                transform: translate(21px, 3px);
            }

            [dir=rtl] .switcher input:checked + .thumb-wrapper .thumb {
                transform: translate(-21px, 3px);
            }

            #documentation-toolbar-button:focus-visible:before,
            .switcher:focus-within:before {
                content: '';
                display: block;
                position: absolute;
                top: -2px;
                right: -2px;
                bottom: -2px;
                left: -2px;
                border-radius: 16px;
                outline: #2962FF solid 2px;
            }
        </style>
        <label for="theme-switch" id="theme-switch-label">Dark Mode</label>
        <div class="switcher">
            <input type="checkbox" id="theme-switch" tabindex="-1">
            <span class="thumb-wrapper">
                <span class="track"></span>
                <span class="thumb"></span>
            </span>
        </div>`;
        themeToggleEl.title = 'Toggle theme';
        const checkboxEl = themeToggleEl.querySelector('#theme-switch');
        checkboxEl.checked = this.themeName === 'dark';
        checkboxEl.addEventListener('change', function () {
            const themeToSet = this.checked ? 'dark' : 'light';
            void widget.changeTheme(themeToSet, { disableUndo: true });
            const svgElement = logoutSvgContainer.svgElement;
            if (!isNullOrUndefined(svgElement)) {
                svgElement.style.fill = this.checked ? '#fff' : '#000';
            }
            TvWorkspaceManager.setTheme(themeToSet);
        });
    }
}
export const TradingPlatformApp = new _TradingPlatformApp();
